import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faX } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import InlineFieldTooltip from "../components/InlineFieldTooltip";

const RepairRulesManagement = () => {
  const [repairRules, setRepairRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    corrosionDamageCombinationId: null,
    repairProcessTypeId: null,
    isRequired: true,
    sequenceOrder: 1,
    stripingProportion: null,
    notes: "",
  });

  // Options for dropdowns
  const [corrosionDamageCombinations, setCorrosionDamageCombinations] =
    useState([]);
  const [repairProcessTypes, setRepairProcessTypes] = useState([]);
  const [layerOptions, setLayerOptions] = useState([]);
  const [bridges, setBridges] = useState([]);
  const [selectedBridgeId, setSelectedBridgeId] = useState("");
  const [applyingBridgeDefaultSplit, setApplyingBridgeDefaultSplit] =
    useState(false);
  const [defaultSplitPctByCombinationId, setDefaultSplitPctByCombinationId] =
    useState({});
  const [creatingProcessCellKey, setCreatingProcessCellKey] = useState("");

  // Matrix data structure
  const [matrixData, setMatrixData] = useState({});
  // Pagination for matrix view (50 rows per page)
  const [matrixPagination, setMatrixPagination] = useState({
    page: 1,
    limit: 50,
  });

  // Fixed order mapping: Wash=1, Prime=2, Stripe=3, Midcoat=4, Topcoat=5
  const getProcessOrder = useCallback((processName) => {
    const orderMap = {
      Wash: 1,
      Prime: 2,
      Stripe: 3,
      Midcoat: 4,
      Topcoat: 5,
    };
    return orderMap[processName] || 999;
  }, []);

  const normalizeProcessKey = useCallback((value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");

    if (normalized.includes("wash")) return "wash";
    if (normalized.includes("prime")) return "prime";
    if (normalized.includes("strip")) return "stripe";
    if (normalized.includes("mid")) return "midcoat";
    if (normalized.includes("top")) return "topcoat";
    return normalized;
  }, []);

  // Sort process types in fixed order
  const sortProcessTypes = useCallback(
    (processTypes) => {
      return [...processTypes].sort((a, b) => {
        const orderA = getProcessOrder(a.processName);
        const orderB = getProcessOrder(b.processName);
        return orderA - orderB;
      });
    },
    [getProcessOrder],
  );

  const getCombinationCode = useCallback((combination) => {
    return String(combination?.combinationCode || "")
      .trim()
      .toUpperCase();
  }, []);

  const getCombinationCategoryCode = useCallback(
    (combination) => {
      const categoryCode = String(
        combination?.corrosionCategory?.categoryCode || "",
      )
        .trim()
        .toUpperCase();
      if (categoryCode) return categoryCode;

      return getCombinationCode(combination).replace(/[ABC]$/, "");
    },
    [getCombinationCode],
  );

  const getCombinationDamageCode = useCallback(
    (combination) => {
      const severityCode = String(
        combination?.damageSeverity?.severityCode || "",
      )
        .trim()
        .toUpperCase();
      if (severityCode) return severityCode;

      const codeMatch = getCombinationCode(combination).match(/([ABC])$/);
      return codeMatch ? codeMatch[1] : "";
    },
    [getCombinationCode],
  );

  const getRepairStageLabel = useCallback(
    (combination) => {
      const stageLabelByDamageCode = {
        A: "Touchup",
        B: "Overcoat",
        C: "Recoat",
      };
      return stageLabelByDamageCode[getCombinationDamageCode(combination)] || "";
    },
    [getCombinationDamageCode],
  );

  const getCombinationDisplayLabel = useCallback(
    (combination) => {
      const code = getCombinationCode(combination);
      const stageLabel = getRepairStageLabel(combination);
      return stageLabel ? `${code} (${stageLabel})` : code;
    },
    [getCombinationCode, getRepairStageLabel],
  );

  const getShortCombinationLabel = useCallback((combination) => {
    const displayLabel = getCombinationDisplayLabel(combination);
    const categoryCode = getCombinationCategoryCode(combination);
    const severityCode = getCombinationDamageCode(combination);

    const categoryLabelByCode = {
      C2: "Mild corrosion",
      C3: "Moderate corrosion",
      C5: "Severe corrosion",
    };

    const severityLabelByCode = {
      A: "minor surface damage",
      B: "damage into midcoat",
      C: "damage to zinc/metal",
    };

    const fallbackCategory = String(
      combination?.corrosionCategory?.categoryName || "",
    )
      .replace(/^Corrosion\s*Category\s*/i, "")
      .trim();

    const fallbackSeverity = String(
      combination?.damageSeverity?.severityName || "",
    )
      .replace(/^[A-Z]\s*-\s*/i, "")
      .trim();

    const categoryText =
      categoryLabelByCode[categoryCode] ||
      (fallbackCategory ? `${fallbackCategory} corrosion` : "");
    const severityText = severityLabelByCode[severityCode] || fallbackSeverity;

    const details = [categoryText, severityText].filter(Boolean).join(", ");
    return details ? `${displayLabel} - ${details}` : displayLabel;
  }, [
    getCombinationCategoryCode,
    getCombinationDamageCode,
    getCombinationDisplayLabel,
  ]);

  // Fetch options for dropdowns
  const fetchOptions = useCallback(async () => {
    try {
      const [combinationsResponse, processTypesResponse, layersResponse] =
        await Promise.all([
          api.get("/corrosion-damage-combinations", { params: { limit: 500 } }),
          api.get("/repair-process-types"),
          api.get("/system-lists", {
            params: { list: "Layers", limit: 500 },
          }),
        ]);

      if (combinationsResponse.data.success) {
        setCorrosionDamageCombinations(combinationsResponse.data.data);
      }
      if (processTypesResponse.data.success) {
        // Touchup and Overcoat cannot be steps in a full repair - exclude from Repair Rules matrix
        const fullRepairOnly = (processTypesResponse.data.data || []).filter(
          (p) =>
            p.processName !== "Touchup" && p.processName !== "Overcoat",
        );
        const sorted = sortProcessTypes(fullRepairOnly);
        setRepairProcessTypes(sorted);
      }

      const layerRows = layersResponse.data?.data || [];
      const sortedLayers = [...layerRows]
        .filter((row) => String(row.value || "").trim())
        .sort(
          (a, b) =>
            Number(a.sortOrder ?? a.sort_order ?? 999) -
            Number(b.sortOrder ?? b.sort_order ?? 999),
        );
      setLayerOptions(sortedLayers);

      try {
        const bridgesResponse = await api.get("/bridges", {
          params: { limit: 500 },
        });
        const bridgeRows = bridgesResponse.data?.data || [];
        setBridges(bridgeRows);
        if (bridgeRows.length > 0) {
          setSelectedBridgeId((prev) => prev || String(bridgeRows[0].id));
        }
      } catch (bridgeError) {
        console.error("Error fetching bridges for default split:", bridgeError);
        setBridges([]);
      }
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  }, [sortProcessTypes]);

  const handleApplyDefaultSplitFromBridge = useCallback(async () => {
    if (!selectedBridgeId) {
      setError("Select a bridge first to auto-calculate default split.");
      return;
    }

    setApplyingBridgeDefaultSplit(true);
    setError(null);
    try {
      const response = await api.post(
        `/corrosion-damage-combinations/default-split/from-bridge/${selectedBridgeId}`,
      );
      const distribution = response.data?.data?.distribution || [];

      const nextMap = {};
      distribution.forEach((row) => {
        nextMap[Number(row.id)] = Number(row.pct || 0);
      });

      setDefaultSplitPctByCombinationId((prev) => ({
        ...prev,
        ...nextMap,
      }));

      setCorrosionDamageCombinations((prev) =>
        prev.map((combo) => {
          const nextPct = nextMap[Number(combo.id)];
          if (nextPct === undefined) return combo;
          return {
            ...combo,
            defaultSplitPct: nextPct,
            default_split_pct: nextPct,
          };
        }),
      );
    } catch (err) {
      console.error("Failed to auto-calculate default split:", err);
      setError(
        err.response?.data?.message ||
          "Failed to auto-calculate default split from bridge area data.",
      );
    } finally {
      setApplyingBridgeDefaultSplit(false);
    }
  }, [selectedBridgeId]);

  useEffect(() => {
    if (!selectedBridgeId) return;
    handleApplyDefaultSplitFromBridge();
  }, [selectedBridgeId, handleApplyDefaultSplitFromBridge]);

  useEffect(() => {
    setDefaultSplitPctByCombinationId((prev) => {
      const next = {};
      corrosionDamageCombinations.forEach((combo) => {
        const comboId = Number(combo.id);
        if (!comboId) return;
        const existing = prev[comboId];
        const fromDb =
          combo.defaultSplitPct !== undefined && combo.defaultSplitPct !== null
            ? combo.defaultSplitPct
            : combo.default_split_pct;
        next[comboId] =
          existing !== undefined && existing !== null
            ? existing
            : (fromDb ?? 0);
      });
      return next;
    });
  }, [corrosionDamageCombinations]);

  const matrixColumns = useMemo(() => {
    const processTypeByKey = new Map();
    (repairProcessTypes || []).forEach((processType) => {
      const key = normalizeProcessKey(processType.processName);
      if (!processTypeByKey.has(key)) {
        processTypeByKey.set(key, processType);
      }
    });

    const columns = [];
    const washProcessType = processTypeByKey.get("wash");
    columns.push({
      key: "wash",
      label: "Wash",
      processTypeId: washProcessType?.id || null,
      processName: washProcessType?.processName || "Wash",
    });

    const seen = new Set(["wash"]);
    (layerOptions || []).forEach((layer) => {
      const layerLabel = String(layer.value || "").trim();
      const key = normalizeProcessKey(layerLabel);
      if (!key || seen.has(key)) return;
      seen.add(key);

      const matched = processTypeByKey.get(key);
      columns.push({
        key,
        label: layerLabel,
        processTypeId: matched?.id || null,
        processName: matched?.processName || layerLabel,
      });
    });

    return columns;
  }, [layerOptions, normalizeProcessKey, repairProcessTypes]);

  const sortedMatrixRows = useMemo(() => {
    const damageOrder = { A: 1, B: 2, C: 3 };
    const categoryOrder = { C2: 1, C3: 2, C5: 3 };

    return Object.values(matrixData).sort((firstRow, secondRow) => {
      const firstDamageCode = getCombinationDamageCode(firstRow.combination);
      const secondDamageCode = getCombinationDamageCode(secondRow.combination);
      const firstDamageOrder = damageOrder[firstDamageCode] || 999;
      const secondDamageOrder = damageOrder[secondDamageCode] || 999;
      if (firstDamageOrder !== secondDamageOrder) {
        return firstDamageOrder - secondDamageOrder;
      }

      const firstCategoryCode = getCombinationCategoryCode(firstRow.combination);
      const secondCategoryCode = getCombinationCategoryCode(
        secondRow.combination,
      );
      const firstCategoryOrder = categoryOrder[firstCategoryCode] || 999;
      const secondCategoryOrder = categoryOrder[secondCategoryCode] || 999;
      if (firstCategoryOrder !== secondCategoryOrder) {
        return firstCategoryOrder - secondCategoryOrder;
      }

      return getCombinationCode(firstRow.combination).localeCompare(
        getCombinationCode(secondRow.combination),
        undefined,
        { numeric: true },
      );
    });
  }, [
    getCombinationCategoryCode,
    getCombinationCode,
    getCombinationDamageCode,
    matrixData,
  ]);

  const defaultSplitTotal = useMemo(
    () =>
      corrosionDamageCombinations.reduce((sum, combo) => {
        const value = Number(defaultSplitPctByCombinationId[combo.id] || 0);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0),
    [corrosionDamageCombinations, defaultSplitPctByCombinationId],
  );

  // Build matrix data structure
  const buildMatrixData = (rules, combinations, processTypes) => {
    const matrix = {};

    // Initialize matrix with all combinations
    combinations.forEach((combination) => {
      matrix[combination.id] = {
        combination: combination,
        processes: {},
      };

      // Initialize all process types as not required
      processTypes.forEach((processType) => {
        matrix[combination.id].processes[processType.id] = {
          processType: processType,
          isRequired: false,
          sequenceOrder: null,
          stripingProportion: null,
          notes: "",
          ruleId: null,
        };
      });
    });

    // Populate matrix with existing rules
    rules.forEach((rule) => {
      const combinationId = rule.corrosionDamageCombinationId;
      const processTypeId = rule.repairProcessTypeId;

      if (
        matrix[combinationId] &&
        matrix[combinationId].processes[processTypeId]
      ) {
        matrix[combinationId].processes[processTypeId] = {
          processType: processTypes.find((p) => p.id === processTypeId),
          isRequired: rule.isRequired,
          sequenceOrder: rule.sequenceOrder,
          stripingProportion: rule.stripingProportion,
          notes: rule.notes || "",
          ruleId: rule.id,
        };
      }
    });

    return matrix;
  };

  // Fetch repair rules
  const fetchRepairRules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/repair-rules-new?limit=500");

      if (response.data.success) {
        setRepairRules(response.data.data);

        // Build matrix data if we have both combinations and process types
        if (
          corrosionDamageCombinations.length > 0 &&
          repairProcessTypes.length > 0
        ) {
          const matrix = buildMatrixData(
            response.data.data,
            corrosionDamageCombinations,
            repairProcessTypes,
          );
          setMatrixData(matrix);
        }
      }
    } catch (error) {
      console.error("Error fetching repair rules:", error);
      setError("Failed to fetch repair rules");
    } finally {
      setLoading(false);
    }
  }, [corrosionDamageCombinations, repairProcessTypes]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    fetchRepairRules();
  }, [fetchRepairRules]);

  // Rebuild matrix when combinations, process types, or rules change
  useEffect(() => {
    if (
      corrosionDamageCombinations.length > 0 &&
      repairProcessTypes.length > 0 &&
      repairRules.length > 0
    ) {
      const matrix = buildMatrixData(
        repairRules,
        corrosionDamageCombinations,
        repairProcessTypes,
      );
      setMatrixData(matrix);
    }
  }, [corrosionDamageCombinations, repairProcessTypes, repairRules]);

  const resetForm = () => {
    setFormData({
      corrosionDamageCombinationId: "",
      repairProcessTypeId: "",
      isRequired: true,
      sequenceOrder: 1,
      stripingProportion: null,
      notes: "",
    });
    setEditingRule(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRule) {
        await api.put(`/repair-rules-new/${editingRule.id}`, formData);
      } else {
        await api.post("/repair-rules-new", formData);
      }

      setShowModal(false);
      resetForm();
      fetchRepairRules();
    } catch (error) {
      console.error("Error saving repair rule:", error);
      setError("Failed to save repair rule");
    }
  };

  // Handle matrix cell click
  const handleMatrixCellClick = (combinationId, processTypeId) => {
    const cellData = matrixData[combinationId]?.processes[processTypeId];
    const processType = repairProcessTypes.find((p) => p.id === processTypeId);

    // Auto-calculate sequence order based on process type
    const autoSequenceOrder = getProcessOrder(processType?.processName || "");

    if (cellData) {
      setFormData({
        corrosionDamageCombinationId: combinationId,
        repairProcessTypeId: processTypeId,
        isRequired: cellData.isRequired,
        sequenceOrder: autoSequenceOrder, // Use fixed order
        stripingProportion: cellData.stripingProportion,
        notes: cellData.notes,
      });
      setEditingRule(cellData.ruleId ? { id: cellData.ruleId } : null);
    } else {
      setFormData({
        corrosionDamageCombinationId: combinationId,
        repairProcessTypeId: processTypeId,
        isRequired: true,
        sequenceOrder: autoSequenceOrder, // Use fixed order
        stripingProportion: null,
        notes: "",
      });
      setEditingRule(null);
    }
    setShowModal(true);
  };

  const handleMissingProcessCellClick = async (combinationId, column) => {
    const cellKey = `${combinationId}-${column.key}`;
    setCreatingProcessCellKey(cellKey);
    setError(null);

    try {
      const response = await api.post("/repair-process-types", {
        process_name: column.label,
        description: `${column.label} process (auto-created from Layers system list)`,
        is_active: true,
      });

      const created = response.data?.data || {};
      const createdId = Number(created.id);
      const createdName =
        String(created.processName || created.process_name || column.label) ||
        column.label;

      if (!Number.isInteger(createdId) || createdId <= 0) {
        throw new Error("Failed to create repair process type.");
      }

      setRepairProcessTypes((prev) => {
        const exists = prev.some((item) => Number(item.id) === createdId);
        if (exists) return prev;
        const next = [...prev, { ...created, processName: createdName }];
        return sortProcessTypes(next);
      });

      setFormData({
        corrosionDamageCombinationId: Number(combinationId),
        repairProcessTypeId: createdId,
        isRequired: true,
        sequenceOrder: getProcessOrder(createdName),
        stripingProportion: null,
        notes: "",
      });
      setEditingRule(null);
      setShowModal(true);
    } catch (err) {
      console.error("Failed to initialize missing process type:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          `Failed to create process type for ${column.label}.`,
      );
    } finally {
      setCreatingProcessCellKey("");
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 mt-2 flex items-start gap-2">
        <p className="text-gray-600">
          Manage repair process rules for corrosion damage combinations
        </p>
        <InlineFieldTooltip
          page="repair_rules"
          section="page_description"
          fieldKey="repair_rules_overview"
        />
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {(() => {
        const combinationIdsWithRules = new Set(
          (repairRules || []).map((r) => r.corrosionDamageCombinationId),
        );
        const combinationsWithNoRules = (
          corrosionDamageCombinations || []
        ).filter((c) => !combinationIdsWithRules.has(c.id));
        if (combinationsWithNoRules.length === 0) return null;
        const codes = combinationsWithNoRules
          .map((c) => c.combinationCode)
          .filter(Boolean);
        return (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-amber-800 text-sm font-medium">
              The following combinations have no Repair Rules configured:
            </p>
            <p className="text-amber-700 text-sm mt-1">{codes.join(", ")}</p>
            <p className="text-amber-600 text-xs mt-2">
              Click a cell in the matrix below to add rules for each combination
              so simulation and repair projects use the correct process steps.
            </p>
          </div>
        );
      })()}

      {/* Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-700">
            Matrix View - Click any cell to add/edit repair rules
          </h2>
          <p
            className={`text-xs mt-1 ${
              Math.abs(defaultSplitTotal - 100) < 0.0001
                ? "text-emerald-700"
                : "text-amber-700"
            }`}
          >
            Area Distribution Total: {defaultSplitTotal.toFixed(2)}%
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedBridgeId}
            onChange={(e) => setSelectedBridgeId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-56"
            title="Bridge used to auto-calculate area distribution from area data"
          >
            <option value="">Select Bridge</option>
            {bridges.map((bridge) => (
              <option key={`split-bridge-${bridge.id}`} value={bridge.id}>
                {bridge.bridgeName || `Bridge ${bridge.id}`}
              </option>
            ))}
          </select>
          {applyingBridgeDefaultSplit && (
            <span className="text-xs text-emerald-700">
              Applying area split...
            </span>
          )}
        </div>
      </div>

      {/* Matrix View */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-gray-100">
                    Combination
                  </th>
                  <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-gray-100">
                    Area Distribution %
                  </th>
                  {matrixColumns.map((column) => (
                    <th
                      key={`matrix-col-${column.key}`}
                      className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-gray-100"
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedMatrixRows
                  .slice(
                    (matrixPagination.page - 1) * matrixPagination.limit,
                    matrixPagination.page * matrixPagination.limit,
                  )
                  .map((rowData) => (
                    <tr
                      key={rowData.combination.id}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 bg-gray-50">
                        <div className="font-semibold text-base">
                          {getCombinationDisplayLabel(rowData.combination)}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center border-r border-gray-200">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          readOnly
                          value={
                            defaultSplitPctByCombinationId[
                              rowData.combination.id
                            ] ?? ""
                          }
                          className="w-20 px-2 py-1 border border-gray-200 bg-gray-50 rounded text-sm text-right text-gray-700 cursor-default"
                          aria-label={`Area distribution percentage for ${rowData.combination.combinationCode}`}
                        />
                      </td>
                      {matrixColumns.map((column) => {
                        if (!column.processTypeId) {
                          const missingCellKey = `${rowData.combination.id}-${column.key}`;
                          const isCreating =
                            creatingProcessCellKey === missingCellKey;
                          return (
                            <td
                              key={`cell-missing-${rowData.combination.id}-${column.key}`}
                              className="px-2 py-2 text-center border-r border-gray-200"
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  handleMissingProcessCellClick(
                                    rowData.combination.id,
                                    column,
                                  )
                                }
                                disabled={isCreating}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                  isCreating
                                    ? "bg-gray-300 text-gray-500"
                                    : "bg-gray-200 hover:bg-gray-300 text-gray-600"
                                } disabled:opacity-70`}
                                title={`Click to enable ${column.label} and add/edit rule`}
                              >
                                {isCreating ? (
                                  <span className="text-[10px]">...</span>
                                ) : (
                                  <FontAwesomeIcon
                                    icon={faX}
                                    className="text-xs"
                                  />
                                )}
                              </button>
                            </td>
                          );
                        }

                        const cellData =
                          rowData.processes[column.processTypeId];
                        return (
                          <td
                            key={`cell-${rowData.combination.id}-${column.key}`}
                            className="px-2 py-2 text-center border-r border-gray-200"
                          >
                            <div className="flex flex-col items-center space-y-1">
                              <button
                                onClick={() =>
                                  handleMatrixCellClick(
                                    rowData.combination.id,
                                    column.processTypeId,
                                  )
                                }
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                  cellData?.isRequired
                                    ? "bg-green-500 hover:bg-green-600"
                                    : "bg-gray-200 hover:bg-gray-300"
                                }`}
                                title={`Click to ${cellData?.isRequired ? "edit" : "add"} ${column.processName} for ${rowData.combination.combinationCode}`}
                              >
                                {cellData?.isRequired ? (
                                  <FontAwesomeIcon
                                    icon={faCheck}
                                    className="text-white text-xs"
                                  />
                                ) : (
                                  <FontAwesomeIcon
                                    icon={faX}
                                    className="text-gray-600 text-xs"
                                  />
                                )}
                              </button>
                              {cellData?.stripingProportion && (
                                <div className="text-xs text-blue-600 font-semibold">
                                  {cellData.stripingProportion}%
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Matrix pagination */}
          {sortedMatrixRows.length > matrixPagination.limit && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-white">
              <p className="text-sm text-gray-700">
                Showing{" "}
                {(matrixPagination.page - 1) * matrixPagination.limit + 1} to{" "}
                {Math.min(
                  matrixPagination.page * matrixPagination.limit,
                  sortedMatrixRows.length,
                )}{" "}
                of {sortedMatrixRows.length} combinations
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setMatrixPagination((p) => ({ ...p, page: p.page - 1 }))
                  }
                  disabled={matrixPagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm">
                  Page {matrixPagination.page} of{" "}
                  {Math.ceil(sortedMatrixRows.length / matrixPagination.limit)}
                </span>
                <button
                  onClick={() =>
                    setMatrixPagination((p) => ({ ...p, page: p.page + 1 }))
                  }
                  disabled={
                    matrixPagination.page >=
                    Math.ceil(sortedMatrixRows.length / matrixPagination.limit)
                  }
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          {/* Matrix Legend */}
          <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
            <div className="flex flex-wrap gap-4 text-xs text-gray-700">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="text-white text-xs"
                  />
                </div>
                <span className="font-medium">Required Process</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-200 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon
                    icon={faX}
                    className="text-gray-600 text-xs"
                  />
                </div>
                <span className="font-medium">Not Required</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-blue-600 font-semibold">15%</span>
                <span className="font-medium">Striping Proportion</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-600">
                  Fixed Order: Wash → Prime → Stripe → Midcoat → Topcoat
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-600">
                  Rows: A = Touchup, B = Overcoat, C = Recoat
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 mb-4">
                <span>
                  {editingRule ? "Edit Repair Rule" : "Add New Repair Rule"}
                </span>
                <InlineFieldTooltip
                  page="repair_rules"
                  section="modal_titles"
                  fieldKey="repair_rule_form"
                  fallbackText="Create or edit repair rules for process sequencing."
                  title="Repair Rule Form"
                />
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Corrosion Damage Combination *
                    </label>
                    <select
                      required
                      value={formData.corrosionDamageCombinationId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          corrosionDamageCombinationId: parseInt(
                            e.target.value,
                          ),
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Combination</option>
                      {corrosionDamageCombinations.map((combination) => (
                        <option key={combination.id} value={combination.id}>
                          {getShortCombinationLabel(combination)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Repair Process Type *
                    </label>
                    <select
                      required
                      value={formData.repairProcessTypeId}
                      onChange={(e) => {
                        const selectedProcessType = repairProcessTypes.find(
                          (p) => p.id === parseInt(e.target.value),
                        );
                        const autoOrder = getProcessOrder(
                          selectedProcessType?.processName || "",
                        );
                        setFormData({
                          ...formData,
                          repairProcessTypeId: parseInt(e.target.value),
                          sequenceOrder: autoOrder, // Auto-update sequence order
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Process Type</option>
                      {repairProcessTypes.map((processType) => (
                        <option key={processType.id} value={processType.id}>
                          {processType.processName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sequence Order (Fixed)
                    </label>
                    <input
                      type="number"
                      value={formData.sequenceOrder}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                      title="Sequence order is automatically set based on process type (Wash=1, Prime=2, Stripe=3, Midcoat=4, Topcoat=5)"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Automatically set: Wash=1, Prime=2, Stripe=3, Midcoat=4,
                      Topcoat=5
                    </p>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Striping Proportion (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.stripingProportion || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          stripingProportion: e.target.value
                            ? parseFloat(e.target.value)
                            : null,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 15.00"
                    />
                  </div>
                  <div className="mb-4 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Required Process
                    </label>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isRequired}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            isRequired: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        This process is required for this combination
                      </label>
                    </div>
                  </div>
                  <div className="mb-4 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="3"
                      placeholder="Additional notes about this repair rule"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {editingRule ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RepairRulesManagement;
